import type { PhiloGraphQL } from '@philo/data-schema';
import { SECURE_BASE_URL } from '../config';
import { GraphQLResponse } from '../types/response';
import { CATCH_ERROR } from './defaults';
import {
  performAssignExperimentMutation,
  performCreateUnauthenticatedFASTAccount,
  performCreateUnauthenticatedFASTAccountToAsset,
  performEnrollInFASTPlan,
} from './graphql';
import { fetchWithRetry, retryPromise } from './promise';

const parseAPIResponse: any = (response: Response) => (response.ok ? response.json() : {});

export const ASSIGN_ANONYMOUS_EXPERIMENT_URL = `${SECURE_BASE_URL}/experiments/assign_anonymous`;

export const assignAnonymousExperiment = (name: string): Promise<string | void> =>
  fetchWithRetry(ASSIGN_ANONYMOUS_EXPERIMENT_URL, {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ name }),
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then<Record<string, string>>(parseAPIResponse)
    .then(({ assignedVariant }) => assignedVariant)
    .catch(CATCH_ERROR);

type AssignExperimentResponse = GraphQLResponse<Pick<PhiloGraphQL.IMutation, 'assignExperiment'>>;

export const assignRegisteredUserExperiment = (experiment: string): Promise<string | null | void> =>
  retryPromise(() => performAssignExperimentMutation(experiment))
    .then<AssignExperimentResponse>(parseAPIResponse)
    .then(({ error, data }) => {
      if (error) {
        throw new Error(error);
      }
      return data?.assignExperiment?.preferences?.[0]?.value;
    })
    .catch(CATCH_ERROR);

interface EnrollInFastPlanResult {
  __typename: 'EnrollInFastPlanResult';
  enrollInFastPlan: {
    fastPlanState: PhiloGraphQL.FastPlanState;
  };
}

interface EnsureAccessToFastAssetResult {
  __typename: 'EnsureAccessToFastAssetResult';
  ensureAccessToFastAsset: {
    asset: {
      fastAccess: boolean;
      playableUniversalURL: string;
    };
  };
}

type EnrollInFastPlanResponse = GraphQLResponse<EnrollInFastPlanResult>;
type EnsureAccessToFastAssetResponse = GraphQLResponse<EnsureAccessToFastAssetResult>;

export const handleEnableFASTAccess = (onSuccess: VoidFunction): Promise<void> =>
  retryPromise(() => performEnrollInFASTPlan())
    .then<EnrollInFastPlanResponse>(parseAPIResponse)
    .then(({ error }) => {
      if (error) {
        throw new Error(error);
      }
      onSuccess();
    })
    .catch(CATCH_ERROR);

export interface Device {
  type: string;
  ident: string;
}

export const handleCreateUncredentialedFASTAccount = (
  deviceType: string,
  deviceIdent: string,
  onSuccess: VoidFunction,
) =>
  retryPromise(() => performCreateUnauthenticatedFASTAccount(deviceType, deviceIdent))
    .then<Record<string, string>>(parseAPIResponse)
    .then(({ error }) => {
      if (error) {
        throw new Error(error);
      }
      onSuccess();
    })
    .catch(CATCH_ERROR);

export const handleCreateUncredentialedFASTAccountToAsset = (
  assetId: string,
  deviceType: string,
  deviceIdent: string,
  onSuccess: (response: string | undefined) => void,
) =>
  retryPromise(() =>
    performCreateUnauthenticatedFASTAccountToAsset(assetId, deviceType, deviceIdent),
  )
    .then<EnsureAccessToFastAssetResponse>(parseAPIResponse)
    .then(({ error, data }) => {
      if (error) {
        throw new Error(error);
      }
      onSuccess(data?.ensureAccessToFastAsset?.asset?.playableUniversalURL);
    })
    .catch(CATCH_ERROR);
